import type { StudioMcpBus } from './studio-mcp-bus'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import type { LoadedApp } from '@/features/page-editor/store/types'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { STUDIO_NS } from './types'

export type AppPatchOp = 'add' | 'remove' | 'replace'

export interface AppPatch {
  op: AppPatchOp
  path: string
  value?: unknown
}

interface PatchTarget {
  parent: Record<string, unknown> | unknown[]
  key: string
  exists: boolean
  value: unknown
}

const ALLOWED_ROOTS = new Set([
  'manifest',
  'blocks',
  'data',
  'flows',
  'tokens',
  'metadata',
  'designSystem',
  'ontology',
])

export function registerAppPatchTool(bus: StudioMcpBus): void {
  bus.registerTool('applyAppPatch', {
    description: 'Apply RFC6902 add/remove/replace patches to the current application Coogo JSON',
    inputSchema: {
      type: 'object',
      properties: {
        patches: {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              op: { type: 'string', enum: ['add', 'remove', 'replace'] },
              path: { type: 'string' },
            },
            required: ['op', 'path'],
          },
        },
      },
      required: ['patches'],
    },
    handler: (payload) => applyAppPatch(payload),
  })
}

export function applyAppPatch(payload: Record<string, unknown>): {
  applied: number
  changedPaths: string[]
  _inverse: { tool: `${typeof STUDIO_NS}applyAppPatch`; params: { patches: AppPatch[] } }
} {
  const patches = normalizePatches(payload.patches)
  if (patches.length === 0) {
    throw new Error('applyAppPatch: patches must be a non-empty array')
  }

  const current = useStudioStore.getState().currentProject as LoadedApp | null
  if (!current) {
    throw new Error('applyAppPatch: no current project is open')
  }

  const next = clone(current) as LoadedApp
  const inverse: AppPatch[] = []
  const changedPaths: string[] = []

  for (const patch of patches) {
    const inv = applySinglePatch(next as unknown as Record<string, unknown>, patch)
    inverse.unshift(inv)
    changedPaths.push(patch.path)
  }

  const validationErrors = validatePatchedApp(next)
  if (validationErrors.length > 0) {
    throw new Error(`applyAppPatch validation failed: ${validationErrors.join('; ')}`)
  }

  useStudioStore.setState((state) => {
    state.currentProject = next as never
    state.isDirty = true
    state.saveError = null
  })
  useEditorStore.getState().loadApp(next)
  toast.success(`Applied ${patches.length} app patch${patches.length === 1 ? '' : 'es'}`)

  return {
    applied: patches.length,
    changedPaths,
    _inverse: {
      tool: `${STUDIO_NS}applyAppPatch`,
      params: { patches: inverse },
    },
  }
}

function normalizePatches(value: unknown): AppPatch[] {
  if (!Array.isArray(value)) return []
  return value.map((patch, index) => {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) {
      throw new Error(`applyAppPatch: patch ${index} must be an object`)
    }
    const raw = patch as Record<string, unknown>
    const op = raw.op
    const path = raw.path
    if (op !== 'add' && op !== 'remove' && op !== 'replace') {
      throw new Error(`applyAppPatch: patch ${index} has unsupported op`)
    }
    if (typeof path !== 'string' || !path.startsWith('/')) {
      throw new Error(`applyAppPatch: patch ${index} path must be a JSON pointer`)
    }
    if ((op === 'add' || op === 'replace') && !('value' in raw)) {
      throw new Error(`applyAppPatch: patch ${index} requires value`)
    }
    return { op, path, value: raw.value }
  })
}

function applySinglePatch(app: Record<string, unknown>, patch: AppPatch): AppPatch {
  assertAllowedRoot(patch.path)
  const target = resolveTarget(app, patch.path)
  const inversePath = canonicalTargetPath(patch.path, target)

  if (patch.op === 'add') {
    const inverse: AppPatch = target.exists && !Array.isArray(target.parent)
      ? { op: 'replace', path: inversePath, value: clone(target.value) }
      : { op: 'remove', path: inversePath }
    addTarget(target, patch.value)
    return inverse
  }

  if (patch.op === 'replace') {
    if (!target.exists) throw new Error(`applyAppPatch: cannot replace missing path ${patch.path}`)
    const inverse: AppPatch = { op: 'replace', path: inversePath, value: clone(target.value) }
    writeTarget(target, patch.value)
    return inverse
  }

  if (!target.exists) throw new Error(`applyAppPatch: cannot remove missing path ${patch.path}`)
  const inverse: AppPatch = { op: 'add', path: inversePath, value: clone(target.value) }
  removeTarget(target)
  return inverse
}

function assertAllowedRoot(path: string): void {
  const root = parsePointer(path)[0]
  if (!root || !ALLOWED_ROOTS.has(root)) {
    throw new Error(`applyAppPatch: path root "${root ?? ''}" is not allowed`)
  }
}

function resolveTarget(app: Record<string, unknown>, path: string): PatchTarget {
  const parts = parsePointer(path)
  if (parts.length === 0) {
    throw new Error('applyAppPatch: replacing the app root is not supported')
  }

  let cursor: unknown = app
  for (const part of parts.slice(0, -1)) {
    if (Array.isArray(cursor)) {
      const index = parseArrayIndex(part, cursor.length, false)
      cursor = cursor[index]
    } else if (isRecord(cursor)) {
      cursor = cursor[part]
    } else {
      throw new Error(`applyAppPatch: parent path is not traversable for ${path}`)
    }
  }

  if (!Array.isArray(cursor) && !isRecord(cursor)) {
    throw new Error(`applyAppPatch: target parent is not an object or array for ${path}`)
  }

  const key = parts[parts.length - 1]
  if (Array.isArray(cursor)) {
    const exists = key !== '-' && isArrayIndex(key) && Number(key) < cursor.length
    const index = key === '-' ? String(cursor.length) : key
    return {
      parent: cursor,
      key: index,
      exists,
      value: exists ? cursor[Number(index)] : undefined,
    }
  }

  return {
    parent: cursor,
    key,
    exists: Object.prototype.hasOwnProperty.call(cursor, key),
    value: cursor[key],
  }
}

function addTarget(target: PatchTarget, value: unknown): void {
  if (Array.isArray(target.parent)) {
    const index = parseArrayIndex(target.key, target.parent.length, true)
    target.parent.splice(index, 0, value)
    return
  }
  target.parent[target.key] = value
}

function writeTarget(target: PatchTarget, value: unknown): void {
  if (Array.isArray(target.parent)) {
    const index = parseArrayIndex(target.key, target.parent.length, true)
    if (index > target.parent.length) {
      throw new Error(`applyAppPatch: array add index ${target.key} is out of range`)
    }
    if (target.exists) target.parent[index] = value
    else target.parent.splice(index, 0, value)
    return
  }
  target.parent[target.key] = value
}

function removeTarget(target: PatchTarget): void {
  if (Array.isArray(target.parent)) {
    if (!target.exists) throw new Error(`applyAppPatch: array remove index ${target.key} is out of range`)
    target.parent.splice(Number(target.key), 1)
    return
  }
  delete target.parent[target.key]
}

function canonicalTargetPath(path: string, target: PatchTarget): string {
  const parts = parsePointer(path)
  parts[parts.length - 1] = target.key
  return `/${parts.map(escapePointerPart).join('/')}`
}

function validatePatchedApp(app: LoadedApp): string[] {
  const errors: string[] = []
  if (!isRecord(app.manifest)) errors.push('manifest must be an object')
  if (!isRecord(app.blocks)) errors.push('blocks must be an object')
  if (errors.length > 0) return errors

  const pages = (app.manifest as unknown as Record<string, unknown>).pages
  if (pages !== undefined && !Array.isArray(pages)) {
    errors.push('manifest.pages must be an array')
    return errors
  }

  for (const [blockName, block] of Object.entries(app.blocks ?? {})) {
    if (!isRecord(block)) {
      errors.push(`block ${blockName} must be an object`)
      continue
    }
    if (!isRecord(block.ui) || !isRecord(block.ui.root)) {
      errors.push(`block ${blockName} must include ui.root`)
    }
  }

  for (const [pageIndex, page] of (pages ?? []).entries()) {
    if (!isRecord(page)) {
      errors.push(`manifest.pages[${pageIndex}] must be an object`)
      continue
    }
    const blocks = page.blocks
    if (!Array.isArray(blocks)) {
      errors.push(`manifest.pages[${pageIndex}].blocks must be an array`)
      continue
    }
    for (const blockRef of blocks) {
      if (typeof blockRef !== 'string') {
        errors.push(`manifest.pages[${pageIndex}].blocks contains a non-string block reference`)
      } else if (!app.blocks?.[blockRef]) {
        errors.push(`manifest.pages[${pageIndex}] references missing block "${blockRef}"`)
      }
    }
  }

  return errors
}

function parsePointer(path: string): string[] {
  if (path === '/') return ['']
  return path
    .slice(1)
    .split('/')
    .map((part) => part.replace(/~1/g, '/').replace(/~0/g, '~'))
}

function escapePointerPart(value: string): string {
  return value.replace(/~/g, '~0').replace(/\//g, '~1')
}

function parseArrayIndex(value: string, length: number, allowEnd: boolean): number {
  if (!isArrayIndex(value)) {
    throw new Error(`applyAppPatch: invalid array index "${value}"`)
  }
  const index = Number(value)
  const max = allowEnd ? length : length - 1
  if (index < 0 || index > max) {
    throw new Error(`applyAppPatch: array index ${value} is out of range`)
  }
  return index
}

function isArrayIndex(value: string): boolean {
  return /^(0|[1-9][0-9]*)$/.test(value)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function clone<T>(value: T): T {
  return typeof structuredClone === 'function'
    ? structuredClone(value)
    : JSON.parse(JSON.stringify(value)) as T
}
